// ServicesPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SavvytronHeader from "../components/SavvytronHeader";
import TechStackSection from "../components/TechStackSection";

export default function ServicesPage() {
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  // Simple scroll-in animation for sections
  useEffect(() => {
    const els = document.querySelectorAll(".section-reveal");
    const onScroll = () => {
      els.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 120) {
          el.classList.add("active");
        }
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const coreServices = [
    {
      id: "custom-software",
      label: "Custom Business Software",
      short:
        "Internal tools, CRMs, data systems, and dashboards built around your workflow.",
      bullets: [
        "Replace spreadsheets and manual processes with one clean system.",
        "Role-based access, audit-friendly, and ready to scale.",
        "Perfect for operations, finance, logistics, and reporting.",
      ],
    },
    {
      id: "websites",
      label: "Websites That Convert",
      short:
        "Fast, modern, mobile-first websites designed for clarity and conversions.",
      bullets: [
        "Clear messaging, strong calls-to-action, and clean navigation.",
        "SEO-ready structure and performance-focused builds.",
        "Ideal for service businesses, professionals, and local brands.",
      ],
    },
    {
      id: "mobile-apps",
      label: "Web & Mobile Apps",
      short:
        "React & React Native apps for customers, staff, or entire product lines.",
      bullets: [
        "Customer portals, booking apps, dashboards, and more.",
        "Available on web, iOS, and Android with a unified codebase.",
        "Secure auth, payments, and push notifications when needed.",
      ],
    },
    {
      id: "automation",
      label: "Workflow Automation & Data",
      short:
        "Automated data flows, integrations, and reporting that save hours every week.",
      bullets: [
        "Connect forms, CRMs, payment systems, and analytics.",
        "Scheduled jobs, notifications, and approvals built-in.",
        "Dashboards that show what’s happening in real time.",
      ],
    },
    {
      id: "ecommerce",
      label: "E-Commerce & Online Ordering",
      short:
        "Online stores and ordering systems that are easy to browse and trust.",
      bullets: [
        "Product catalogs, carts, checkout, and payment integrations.",
        "Tailored for shops, services, and subscription-style businesses.",
        "Analytics and reporting so you know what’s working.",
      ],
    },
    {
      id: "care",
      label: "Hosting, Security & Long-Term Care",
      short:
        "Monitoring, backups, updates, and support so your systems stay healthy.",
      bullets: [
        "Secure hosting with SSL, updates, and patches handled for you.",
        "Performance checks and uptime monitoring.",
        "On-call support for issues, tweaks, or new feature requests.",
      ],
    },
  ];

  const processSteps = [
    {
      title: "Discovery & Planning",
      text: "We talk through your goals, current process, and what “success” should look like. Then I translate that into a clear scope.",
    },
    {
      title: "Design & Blueprint",
      text: "You see flows, simple screens, and data structures before we write real code, so everything is aligned.",
    },
    {
      title: "Build, Test & Iterate",
      text: "I build in small, reviewable pieces with regular check-ins and staging demos so there are no surprises.",
    },
    {
      title: "Launch & Support",
      text: "We deploy, monitor, and support your product — with options for ongoing maintenance and future improvements.",
    },
  ];

  const whyPoints = [
    {
      title: "Engineer First, Not Agency",
      text: "You work directly with the person designing and building your system — not an account manager relaying messages.",
    },
    {
      title: "Clear Communication",
      text: "Simple language, shared plans, and transparent timelines. You always know what’s happening and what’s next.",
    },
    {
      title: "Long-Term Partnership",
      text: "I’m not interested in quick one-off projects. The goal is to become your reliable tech partner as you grow.",
    },
    {
      title: "Modern, Stable Stack",
      text: "MERN, AWS, Postgres, Python and a carefully chosen set of tools that are proven in real-world products.",
    },
  ];

  const miniCases = [
    {
      tag: "Workflow Automation",
      title: "Cut 60% of Manual Data Entry",
      text: "Replaced multi-sheet spreadsheets and email-based approvals with a central dashboard, automated imports, and one source of truth.",
    },
    {
      tag: "Online Ordering",
      title: "Turned Phone Orders into 24/7 Requests",
      text: "Built an online request system with structured forms and automated notifications so staff could focus on fulfillment, not message chasing.",
    },
  ];

  const faqs = [
    {
      q: "What kind of businesses do you usually work with?",
      a: "Service businesses, local shops, solo founders, and small teams that need reliable systems — not just a “pretty website”. If your business runs on processes, data, or bookings, I can probably help.",
    },
    {
      q: "How long does a typical project take?",
      a: "Smaller sites and tools can be delivered in 2–4 weeks. More complex systems, dashboards or apps usually take 4–8+ weeks depending on scope.",
    },
    {
      q: "Do you offer ongoing maintenance?",
      a: "Yes. I can host, monitor, update, and extend anything I build so you’re not left “holding the code”. We can set up a simple monthly care plan.",
    },
    {
      q: "I’m not sure exactly what I need. Is that okay?",
      a: "Absolutely. The discovery call is designed to translate your goals into something concrete and technical. You don’t need to come with a fully written spec.",
    },
    {
      q: "How do payments and pricing work?",
      a: "Most projects are fixed-price based on a clear scope, with 2–3 milestone payments. For ongoing work, we can use retainers or a simple hourly model.",
    },
  ];

  return (
    <div className="min-h-screen text-gray-800 font-sans bg-gray-50/40">
      {/* Small local styles for section fade-in + FAQ */}
      <style>{`
        .section-reveal {
          opacity: 0;
          transform: translateY(18px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .section-reveal.active {
          opacity: 1;
          transform: translateY(0);
        }
        .faq-item {
          border-radius: 0.75rem;
          border: 1px solid rgba(209,213,219,0.9);
          background: rgba(255,255,255,0.9);
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
        }
        .faq-item.open {
          border-color: rgba(120,95,20,0.55);
          box-shadow: 0 10px 25px rgba(0,0,0,0.06);
          background: white;
        }
      `}</style>

      {/* Sticky header */}
      <SavvytronHeader />

      {/* Page shell */}
      <main className="max-w-5xl mx-auto px-4 pb-16 pt-6 md:pt-10">
        <div className="bg-white/90 md:p-10 p-6">
          {/* HERO */}
          <section className="section-reveal">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              {/* Left */}
              <div className="flex-1">
                <p className="uppercase tracking-[0.2em] text-xs text-[#785f14] mb-3">
                  Services
                </p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl  text-gray-700 leading-snug">
                  Digital Services Built
                  <span className="block text-[#785f14] mt-1 font-copperplate">
                    To Run Your Business
                  </span>
                </h1>

                <p className="mt-4 text-gray-600 text-sm sm:text-base max-w-xl">
                  From first website to full internal tools, SavvyTron helps you
                  design, build, and maintain reliable software that supports
                  your day-to-day work — not the other way around.
                </p>

                <ul className="mt-4 space-y-2 text-sm text-gray-700">
                  <li className="flex gap-2">
                    <span className="mt-[3px] text-[#785f14]">✔</span>
                    <span>Honest scoping, clear timelines, and no jargon.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-[3px] text-[#785f14]">✔</span>
                    <span>Modern engineering best practices, not quick hacks.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-[3px] text-[#785f14]">✔</span>
                    <span>Designed for long-term maintainability and growth.</span>
                  </li>
                </ul>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
<button
  onClick={() => navigate("/contact#book", { replace: false })}
  className="
    px-7 py-3 rounded-lg w-full sm:w-auto
    bg-gradient-to-r from-brand to-brand-hover
    text-white font-medium text-sm shadow-md
    hover:shadow-lg hover:scale-[1.03] active:scale-95
    transition
  "
>
  Book a Free Call
</button>


                  <button
                    onClick={() =>
                      document
                        .getElementById("core-services")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                    className="
                      px-7 py-3 rounded-lg w-full sm:w-auto
                      border border-[#785f14]
                      text-[#785f14] font-medium text-sm
                      hover:bg-[#785f14]/10 transition
                      hover:scale-[1.03] active:scale-95
                    "
                  >
                    View All Services
                  </button>
                </div>
              </div>

              {/* Right visual */}
              <div className="flex-1 flex justify-center">
                <div className="relative w-72 sm:w-80">
                  <div className="absolute -inset-4 bg-[#785f14]/10 blur-3xl rounded-3xl" />
                  <div className="relative bg-gray-50 border border-gray-200 rounded-2xl shadow-lg overflow-hidden p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-gray-500">
                          Example Setup
                        </p>
                        <p className="text-sm font-semibold text-gray-800">
                          Business Control Panel
                        </p>
                      </div>
                      <span className="px-2 py-1 rounded-full text-[10px] bg-[#785f14]/10 text-[#785f14]">
                        Live
                      </span>
                    </div>

                    <div className="space-y-3 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Website Leads</span>
                        <span className="font-semibold text-gray-800">
                          +42%
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">
                          Manual Tasks / Week
                        </span>
                        <span className="font-semibold text-emerald-600">
                          -63%
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Response Time</span>
                        <span className="font-semibold text-gray-800">
                          2.1 hrs
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent" />

                    <div className="mt-4 grid grid-cols-3 gap-2 text-[11px]">
                      <div className="rounded-lg bg-white border border-gray-200 px-2 py-2">
                        <p className="font-semibold text-gray-700">Website</p>
                        <p className="text-gray-500">Leads & inquiries</p>
                      </div>
                      <div className="rounded-lg bg-white border border-gray-200 px-2 py-2">
                        <p className="font-semibold text-gray-700">Dashboard</p>
                        <p className="text-gray-500">Daily ops view</p>
                      </div>
                      <div className="rounded-lg bg-white border border-gray-200 px-2 py-2">
                        <p className="font-semibold text-gray-700">Automation</p>
                        <p className="text-gray-500">Fewer emails</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CORE SERVICES */}
          <section
            id="core-services"
            className="section-reveal mt-16 border-t border-gray-200 pt-10"
          >
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="text-left">
                <h2 className="text-xl sm:text-2xl font-copperplate text-[#785f14]">
                  Core Services
                </h2>
                <p className="text-gray-600 text-sm sm:text-base mt-1 max-w-xl">
                  A focused set of services that cover everything from your
                  public website to the internal tools that keep your business
                  running.
                </p>
              </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              {coreServices.map((service) => (
                <article
                  key={service.id}
                  className="rounded-xl border border-gray-200 bg-white/90 shadow-sm hover:shadow-md transition p-5 text-left"
                >
                  <h3 className="font-semibold text-[#785f14] text-base sm:text-lg mb-1">
                    {service.label}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">{service.short}</p>
                  <ul className="text-gray-600 text-xs sm:text-sm space-y-1.5">
                    {service.bullets.map((b, idx) => (
                      <li key={idx} className="flex gap-2">
                        <span className="mt-[2px] text-[#785f14]">•</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </section>

          {/* PROCESS */}
          <section className="section-reveal mt-16">
            <h2 className="text-xl sm:text-2xl font-copperplate text-[#785f14] text-center">
              How Working Together Feels
            </h2>
            <p className="text-gray-600 text-sm sm:text-base mt-2 text-center max-w-xl mx-auto">
              A simple, transparent process from first conversation to launch
              and beyond.
            </p>

            {/* Desktop timeline */}
            <div className="hidden lg:flex mt-10 gap-8 relative">
              <div className="absolute left-0 right-0 top-8 h-[3px] bg-gradient-to-r from-[#785f14]/30 via-[#785f14]/50 to-[#785f14]/30" />
              {processSteps.map((step, idx) => (
                <div
                  key={step.title}
                  className="flex-1 flex flex-col items-center text-center relative"
                >
                  <div className="relative z-10 flex items-center justify-center w-12 h-12 rounded-full bg-white border border-[#785f14]/40 shadow-sm">
                    <span className="text-sm font-semibold text-[#785f14]">
                      {idx + 1}
                    </span>
                  </div>
                  <h3 className="mt-4 font-semibold text-gray-800 text-sm">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-xs text-gray-600 max-w-xs">
                    {step.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Mobile vertical */}
            <div className="lg:hidden mt-8 space-y-6">
              {processSteps.map((step, idx) => (
                <div key={step.title} className="flex gap-3 items-start">
                  <div className="flex flex-col items-center">
                    <div className="w-9 h-9 rounded-full bg-white border border-[#785f14]/50 flex items-center justify-center shadow-sm">
                      <span className="text-xs font-semibold text-[#785f14]">
                        {idx + 1}
                      </span>
                    </div>
                    {idx < processSteps.length - 1 && (
                      <div className="w-[2px] flex-1 bg-[#785f14]/30 mt-1" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 text-sm">
                      {step.title}
                    </h3>
                    <p className="mt-1 text-xs text-gray-600">
                      {step.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* WHY CHOOSE */}
          <section className="section-reveal mt-16">
            <h2 className="text-xl sm:text-2xl font-copperplate text-[#785f14] text-center">
              Why Teams Choose SavvyTron
            </h2>
            <p className="text-gray-600 text-sm sm:text-base mt-2 text-center max-w-xl mx-auto">
              Hiring a developer is about trust. These are the principles I use
              on every project.
            </p>

            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              {whyPoints.map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl bg-white/90 border border-gray-200 shadow-sm p-5 hover:shadow-md transition"
                >
                  <h3 className="font-semibold text-gray-800 text-sm mb-1">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* MINI CASE STUDIES */}
          <section className="section-reveal mt-16">
            <h2 className="text-xl sm:text-2xl font-copperplate text-[#785f14] text-center">
              Real Problems, Practical Outcomes
            </h2>
            <p className="text-gray-600 text-sm sm:text-base mt-2 text-center max-w-xl mx-auto">
              A few examples of the kind of improvements SavvyTron-style
              solutions create.
            </p>

            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {miniCases.map((c) => (
                <article
                  key={c.title}
                  className="rounded-xl bg-white/90 border border-gray-200 shadow-sm p-5"
                >
                  <span className="inline-flex text-[11px] uppercase tracking-[0.18em] text-[#785f14] bg-[#785f14]/10 rounded-full px-3 py-1 mb-3">
                    {c.tag}
                  </span>
                  <h3 className="font-semibold text-gray-800 text-sm mb-2">
                    {c.title}
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm">{c.text}</p>
                </article>
              ))}
            </div>
          </section>

          {/* PRICING SUMMARY */}
          <section className="section-reveal mt-16">
            <h2 className="text-xl sm:text-2xl font-copperplate text-[#785f14] text-center">
              Ways We Can Work Together
            </h2>
            <p className="text-gray-600 text-sm sm:text-base mt-2 text-center max-w-xl mx-auto">
              Every project is scoped individually, but most fits into one of
              these shapes.
            </p>

            <div className="mt-8 grid gap-6 md:grid-cols-3 text-left">
              <div className="rounded-xl bg-white/90 border border-gray-200 shadow-sm p-5 flex flex-col">
                <h3 className="font-semibold text-gray-800 text-base mb-1">
                  Launch-Ready Website
                </h3>
                <p className="text-gray-600 text-xs mb-3 flex-1">
                  Ideal for service businesses or professionals that need a
                  sharp, trust-building online presence.
                </p>
                <ul className="text-xs text-gray-600 space-y-1 mb-4">
                  <li>• 3–6 core pages</li>
                  <li>• Contact & lead forms</li>
                  <li>• Basic analytics setup</li>
                </ul>
                <p className="text-[11px] text-gray-500">
                  Typical timeline:{" "}
                  <span className="font-semibold text-gray-700">2–4 weeks</span>
                </p>
              </div>

              <div className="rounded-xl bg-[#785f14]/5 border border-[#785f14]/40 shadow-sm p-5 flex flex-col">
                <h3 className="font-semibold text-gray-800 text-base mb-1">
                  Business Tools & Automation
                </h3>
                <p className="text-gray-700 text-xs mb-3 flex-1">
                  For teams that want dashboards, internal tools, or integrated
                  workflows that reduce manual work.
                </p>
                <ul className="text-xs text-gray-700 space-y-1 mb-4">
                  <li>• Custom dashboards & data views</li>
                  <li>• Integrations & job scheduling</li>
                  <li>• Role-based access and logins</li>
                </ul>
                <p className="text-[11px] text-gray-600">
                  Typical timeline:{" "}
                  <span className="font-semibold text-gray-800">
                    4–8+ weeks
                  </span>
                </p>
              </div>

              <div className="rounded-xl bg-white/90 border border-gray-200 shadow-sm p-5 flex flex-col">
                <h3 className="font-semibold text-gray-800 text-base mb-1">
                  Custom Product / App
                </h3>
                <p className="text-gray-600 text-xs mb-3 flex-1">
                  For founders and teams building something new — a SaaS, a
                  customer portal, or a full product.
                </p>
                <ul className="text-xs text-gray-600 space-y-1 mb-4">
                  <li>• Discovery, design, and build</li>
                  <li>• Web and/or mobile apps</li>
                  <li>• Ongoing improvements and support</li>
                </ul>
                <p className="text-[11px] text-gray-500">
                  Typical timeline:{" "}
                  <span className="font-semibold text-gray-700">
                    8–12+ weeks
                  </span>
                </p>
              </div>
            </div>

            <div className="text-center mt-6">
              <button
                onClick={() => navigate("/plans")}
                className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg border border-[#785f14] text-[#785f14] text-sm font-medium hover:bg-[#785f14]/10 transition"
              >
                Learn more about pricing & plans
              </button>
            </div>
          </section>

          {/* TECH STACK CLUSTER */}
          <section className="section-reveal mt-16">
            <TechStackSection />
          </section>

          {/* FAQ */}
          <section className="section-reveal mt-16">
            <h2 className="text-xl sm:text-2xl font-copperplate text-[#785f14] text-center">
              Questions, Answered
            </h2>
            <p className="text-gray-600 text-sm sm:text-base mt-2 text-center max-w-xl mx-auto">
              Still unsure if this is the right fit? These might help — and we
              can always talk it through on a quick call.
            </p>

            <div className="mt-8 space-y-3">
              {faqs.map((item, idx) => {
                const open = openFaqIndex === idx;
                return (
                  <div
                    key={item.q}
                    className={`faq-item ${open ? "open" : ""} cursor-pointer`}
                    onClick={() =>
                      setOpenFaqIndex((prev) => (prev === idx ? -1 : idx))
                    }
                  >
                    <div className="flex justify-between items-center px-4 py-3">
                      <p className="text-sm font-medium text-gray-800">
                        {item.q}
                      </p>
                      <span className="ml-3 text-xs text-gray-500">
                        {open ? "−" : "+"}
                      </span>
                    </div>
                    {open && (
                      <div className="px-4 pb-3 text-xs sm:text-sm text-gray-600 border-t border-gray-100">
                        <p className="pt-2">{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="section-reveal mt-16 text-center border-t border-gray-200 pt-10">
            <h2 className="text-xl sm:text-2xl font-copperplate text-[#785f14]">
              Ready to Talk About Your Project?
            </h2>
            <p className="text-gray-600 text-sm sm:text-base mt-2 max-w-xl mx-auto">
              Tell me where your business is today and where you’d like it to
              be. I’ll help you map the right digital tools to get there —
              without overcomplicating things.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate("/contact#book")}
                className="
                  px-7 py-3 rounded-lg w-full sm:w-auto
                  bg-gradient-to-r from-brand to-brand-hover
                  text-white font-medium text-sm shadow-md
                  hover:shadow-lg hover:scale-[1.03] active:scale-95
                  transition
                "
              >
                Book a Free Discovery Call
              </button>
              <button
                onClick={() => navigate("/contact#message")}
                className="
                  px-7 py-3 rounded-lg w-full sm:w-auto
                  border border-[#785f14]
                  text-[#785f14] font-medium text-sm
                  hover:bg-[#785f14]/10 transition
                  hover:scale-[1.03] active:scale-95
                "
              >
                Send a Project Message
              </button>
            </div>
          </section>
          
          {/* Footer */}
          <footer className="flex flex-col gap-4 mt-8 border-t border-gray-200 pt-6 text-sm text-gray-700 md:flex-row md:justify-between">
            <div className='mb-4 md:mb-0'>
              <p><strong>Email:</strong> contact@savvytron.com</p>
              <p><strong>Location:</strong> Based in Dallas, TX – Available remotely or in-person</p>
              <p><strong>Hours:</strong> Mon–Sat, 10 AM to 6 PM</p>
            </div>

            <div className="md:self-center">
              <a
                href="/login"
                className="
                         w-full sm:w-auto
                          inline-flex items-center justify-center
                        px-6 py-2.5
                             rounded-lg
                                    bg-brand/10           /* 10 % tint of brand colour */
                           text-brand
    font-medium
    transition
    hover:bg-brand/20
    active:bg-brand/25
    focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40
    text-sm sm:text-base
  "
              >
                Client&nbsp;login
              </a>

            </div>



          </footer>
        </div>
      </main>
    </div>
  );
}
